import type { AppDispatch } from "@store/index";
import { datasetApi } from "@store/api/dataset-api";
import type {
  QuestionResponsesSearchFilter,
  QuestionVariablesResponse,
} from "@models/Filter";
import type { VisualizationBase } from "@models/VisualizationBase";
import { getVisualizationBaseSource } from "@models/VisualizationBase";
import { visualizationBaseToSearchFilters } from "./visualization-base-utils";

/**
 * A pre-configured base only stores a reference (preconfiguredBaseId); its
 * conditions live on the dataset. Resolve them from the dataset's current bases
 * so the same downstream transform can run. Returns null if the referenced base
 * no longer exists (treated as "no base"). Custom bases pass through unchanged.
 */
async function resolveEffectiveBase(
  dispatch: AppDispatch,
  datasetId: string,
  base: VisualizationBase,
): Promise<VisualizationBase | null> {
  if (getVisualizationBaseSource(base) !== "preconfigured") return base;
  if (!base.preconfiguredBaseId) return null;

  try {
    const dataset = await dispatch(
      // One-off lookup: reuse the cache but don't hold a subscription.
      datasetApi.endpoints.getDatasetById.initiate(datasetId, {
        subscribe: false,
      }),
    ).unwrap();
    const datasetBase = dataset.bases?.find(
      (b) => b.id === base.preconfiguredBaseId,
    );
    if (!datasetBase) return null;
    return {
      enabled: true,
      name: datasetBase.name,
      conditions: datasetBase.conditions,
    };
  } catch {
    return null;
  }
}

/**
 * Resolves a chart's visualizationBase into /search-ready filters. Fetches
 * (or reuses the RTK Query cache for) each condition's question variables so
 * IDs can be translated to the backend's expected identifiers before calling
 * the pure visualizationBaseToSearchFilters transform.
 */
export async function resolveVisualizationBaseFilters(
  dispatch: AppDispatch,
  datasetId: string | undefined | null,
  rawBase: VisualizationBase | undefined | null,
): Promise<QuestionResponsesSearchFilter[]> {
  if (!datasetId || !rawBase?.enabled) return [];

  const base = await resolveEffectiveBase(dispatch, datasetId, rawBase);
  if (!base || base.conditions.length === 0) return [];

  const uniqueQuestionIds = Array.from(
    new Set(base.conditions.map((c) => c.questionId).filter(Boolean)),
  );

  if (uniqueQuestionIds.length === 0) return [];

  const entries = await Promise.all(
    uniqueQuestionIds.map(async (questionId) => {
      try {
        const result = await dispatch(
          datasetApi.endpoints.getDatasetQuestionVariablesByElement.initiate(
            { id: datasetId, dataset_element_id: questionId },
            // One-off lookup: reuse the cache but don't hold a subscription.
            { subscribe: false },
          ),
        ).unwrap();
        return [questionId, result] as const;
      } catch {
        return [questionId, undefined] as const;
      }
    }),
  );

  const resolvedVariablesByQuestionId: Record<string, QuestionVariablesResponse> = {};
  entries.forEach(([questionId, result]) => {
    if (result) resolvedVariablesByQuestionId[questionId] = result;
  });

  return visualizationBaseToSearchFilters(base, resolvedVariablesByQuestionId);
}
